import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";

import { Button, TextInput } from "@mantine/core";

import { mutateVehicles } from "@/api/vehicle/useVehicleQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { useForm } from "@mantine/form";
import { ScmApiVehiclesVehicleIdPutRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Update = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
});

export const VehicleUpdateForm = (params: { formatterProps: any }) => {
  const { closeModal } = useModal();
  const { formatterProps } = params;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: updateMutate } = useMutation(
    (params: ScmApiVehiclesVehicleIdPutRequest) =>
      mutateVehicles.update(params).mutationFn(params as ScmApiVehiclesVehicleIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        closeModal(formatterProps);
        customAlert(
          t("message.editSuccess", { item: t("vehicle.vehicle") }),
          t("common.editSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("vehicle.code") }),
          t("common.editFail", { item: t("vehicle.vehicle") }),
          "red",
        );
      },
    },
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");

  const form = useForm({
    initialValues: {
      code: formatterProps.code,
      name: formatterProps.name,
      plateNumber: formatterProps.plateNumber,
      driver: formatterProps.driver,
      description: formatterProps.description,
    },
  });

  const onSubmit = () => {
    try {
      updateMutate({
        vehicleId: formatterProps.id as number,
        vehiclesGetRequest: {
          code: form.values.code!,
          name: form.values.name!,
          plateNumber: form.values.plateNumber!,
          driver: formatterProps.driver,
          description: form.values.description,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("vehicle.code") }),
        t("common.editFail", { item: t("vehicle.vehicle") }),
        "red",
      );
    }
  };

  return (
    <>
      <Update.Wrapper>
        <Update.Input
          label={t("vehicle.code")}
          placeholder={t("placeholder.input", { item: `${t("vehicle.code")}` })}
          withAsterisk
          defaultValue={formatterProps?.code}
          {...form.getInputProps("code")}
          error={form.isDirty("code") ? t("common.toSave") : errorMessage ? errorMessage : ""}
          styles={{
            input: {
              color: form.isDirty("code") ? "red !important" : "",
              borderColor: form.isDirty("code") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.Input
          label={t("vehicle.name")}
          placeholder={t("placeholder.input", { item: `${t("vehicle.name")}` })}
          withAsterisk
          defaultValue={formatterProps?.name}
          {...form.getInputProps("name")}
          error={form.isDirty("name") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("name") ? "red !important" : "",
              borderColor: form.isDirty("name") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.Input
          label={t("vehicle.plateNumber")}
          placeholder={t("placeholder.input", { item: t("vehicle.plateNumber") })}
          withAsterisk
          defaultValue={formatterProps?.plateNumber}
          {...form.getInputProps("plateNumber")}
          error={form.isDirty("plateNumber") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("plateNumber") ? "red !important" : "",
              borderColor: form.isDirty("plateNumber") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.Input
          label={t("vehicle.driver")}
          placeholder={t("placeholder.input", { item: t("vehicle.driver") })}
          withAsterisk
          defaultValue={formatterProps?.driver}
          {...form.getInputProps("driver")}
          error={form.isDirty("driver") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("driver") ? "red !important" : "",
              borderColor: form.isDirty("driver") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.Input
          label={t("vehicle.description")}
          placeholder={t("placeholder.input", { item: t("vehicle.description") })}
          withAsterisk
          defaultValue={formatterProps?.description}
          {...form.getInputProps("description")}
          error={form.isDirty("description") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("description") ? "red !important" : "",
              borderColor: form.isDirty("description") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.ButtonBox>
          <Update.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Update.Button>
          <Update.Button
            disabled={validateFormValues({
              pageType: "vehicle",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {t("common.edit", { item: t("vehicle.vehicle") })}
          </Update.Button>
        </Update.ButtonBox>
      </Update.Wrapper>
    </>
  );
};
